import React, { useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { HeartHandshake, Users, CheckCircle, ArrowRight } from 'lucide-react'

const Caretakers = () => {
  useEffect(() => {
    document.title = 'Care Takers - Shrijaa Outsourcing Consultants | Home & Elderly Care Staff'
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Professional care takers for elderly care, patient assistance at home, and daily living support. Shrijaa Outsourcing Consultants provides verified care-taking staff across Hyderabad and beyond.'
      )
    }
  }, [])

  return (
    <div>
      <section className="bg-gradient-to-br from-primary-800 via-primary-700 to-teal-700 text-white py-16 md:py-20">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">Care Takers</h1>
            <p className="text-lg md:text-xl text-primary-100">
              Compassionate care takers for home care, elderly support, and daily assistance — men & women available
            </p>
            <div className="w-20 h-1 bg-accent-500 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
            <div>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center mb-6">
                  <HeartHandshake className="w-12 h-12 text-primary-600 mr-4" />
                  <h2 className="text-3xl md:text-4xl font-bold text-secondary-900">Professional Care Takers</h2>
                </div>
                <p className="text-secondary-600 leading-relaxed mb-6">
                  Shrijaa Outsourcing Consultants supplies trained care takers for families, seniors, and individuals who need reliable support at home. Our staff can assist with daily activities, companionship, and light support under your guidance.
                </p>
                <p className="text-secondary-600 leading-relaxed mb-6">
                  We provide both men and women care takers, background-verified and matched to your requirements whenever possible.
                </p>
              </motion.div>
            </div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-secondary-50 rounded-lg p-8"
            >
              <img
                src="/service-care-takers.avif"
                alt="Care taker with a senior, supportive in-home assistance"
                className="w-full rounded-lg object-cover aspect-[4/3]"
              />
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {[
              {
                icon: <Users className="w-8 h-8" />,
                title: 'Men & Women Available',
                description: 'Care takers matched to your household preferences and care needs.'
              },
              {
                icon: <HeartHandshake className="w-8 h-8" />,
                title: 'Home & Elderly Focus',
                description: 'Support for daily living, companionship, and dignified care at home.'
              },
              {
                icon: <CheckCircle className="w-8 h-8" />,
                title: 'Verified Staff',
                description: 'Background checks and verification for peace of mind.'
              },
              {
                icon: <CheckCircle className="w-8 h-8" />,
                title: 'Flexible Arrangements',
                description: 'Full-time, part-time, or rotational care options where available.'
              },
              {
                icon: <CheckCircle className="w-8 h-8" />,
                title: 'Quick Placement',
                description: 'Fast coordination when you need care taker support urgently.'
              },
              {
                icon: <CheckCircle className="w-8 h-8" />,
                title: 'Dedicated Support',
                description: 'Our team helps you clarify requirements and find suitable care takers.'
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow"
              >
                <div className="text-primary-600 mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-primary-800 mb-3">{feature.title}</h3>
                <p className="text-secondary-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="bg-primary-600 rounded-lg p-8 text-center text-white">
            <h3 className="text-3xl font-bold mb-4">Need Care Takers?</h3>
            <p className="text-xl text-primary-100 mb-6">Contact us to discuss your care-taking requirements</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-primary-600 hover:bg-primary-50 font-semibold py-3 px-8 rounded-lg transition-colors inline-flex items-center justify-center"
              >
                Contact Us
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link
                to="/services"
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold py-3 px-8 rounded-lg transition-colors"
              >
                View All Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Caretakers
